import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, X, ImagePlus, Upload, MapPin, ArrowLeft, Save, Trash2, DollarSign } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORIES, ALL_LOCATIONS } from "@/lib/constants";
import api from "@/lib/api";

export default function EditListing() {
  const { itemId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const [locationQuery, setLocationQuery] = useState("");
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "", images: [],
    price_per_day: "", location: "", location_type: "us",
    delivery_option: "both", condition: "Good",
    for_sale: false, sale_price: "",
  });

  // Fetch existing item data
  useEffect(() => {
    if (!itemId) return;
    api.get(`/items/${itemId}`)
      .then((res) => {
        const item = res.data;
        setForm({
          title: item.title || "",
          description: item.description || "",
          category: item.category || "",
          images: item.images || [],
          price_per_day: item.price_per_day?.toString() || "",
          location: item.location || "",
          location_type: item.location_type || "us",
          delivery_option: item.delivery_option || "both",
          condition: item.condition || "Good",
          for_sale: item.for_sale || false,
          sale_price: item.sale_price?.toString() || "",
        });
        setLocationQuery(item.location || "");
      })
      .catch((err) => {
        toast.error("Failed to load item");
        navigate("/dashboard");
      })
      .finally(() => setFetching(false));
  }, [itemId, navigate]);

  // Handle click outside for location dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationInputRef.current && !locationInputRef.current.contains(e.target)) {
        setShowLocationSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <h2 className="font-heading font-semibold text-xl mb-2">Log in to edit listings</h2>
        <p className="text-stone-500 text-sm mb-4">You need to be logged in.</p>
        <Button onClick={() => navigate("/auth")} className="rounded-full bg-coral hover:bg-coral text-white">Log In</Button>
      </div>
    );
  }

  if (fetching) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-stone-100 rounded w-1/3" />
          <div className="h-12 bg-stone-100 rounded-xl" />
          <div className="h-32 bg-stone-100 rounded-xl" />
        </div>
      </div>
    );
  }

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setLocationQuery(value);
    if (value.trim().length > 0) {
      const filtered = ALL_LOCATIONS.filter((loc) =>
        loc.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 8);
      setFilteredLocations(filtered);
      setShowLocationSuggestions(true);
    } else {
      setShowLocationSuggestions(false);
      setForm({ ...form, location: "" });
    }
  };

  const selectLocation = (location) => {
    setLocationQuery(location.name);
    setForm({ ...form, location: location.name });
    setShowLocationSuggestions(false);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = 5 - form.images.length;
    const filesToUpload = files.slice(0, remainingSlots);
    
    if (files.length > remainingSlots) {
      toast.warning(`Only ${remainingSlots} more image(s) can be added`);
    }

    setUploading(true);
    const newImages = [];

    for (const file of filesToUpload) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await api.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        newImages.push(res.data.url);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (newImages.length > 0) {
      setForm((f) => ({ ...f, images: [...f.images, ...newImages] }));
      toast.success(`${newImages.length} image(s) uploaded`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeImage = (i) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.location || !form.price_per_day) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.images.length < 1) {
      toast.error("Please add at least 1 image");
      return;
    }
    if (form.for_sale && !form.sale_price) {
      toast.error("Please enter a sale price");
      return;
    }
    setLoading(true);
    try {
      const locType = ALL_LOCATIONS.find((l) => l.name === form.location)?.type || "us";
      const payload = {
        ...form,
        price_per_day: parseFloat(form.price_per_day),
        location_type: locType,
        sale_price: form.for_sale ? parseFloat(form.sale_price) : null,
      };
      await api.put(`/items/${itemId}`, payload);
      toast.success("Listing updated!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update listing");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this listing?")) return;
    try {
      await api.delete(`/items/${itemId}`);
      toast.success("Listing deleted");
      navigate("/dashboard");
    } catch (err) {
      toast.error("Failed to delete listing");
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="edit-listing-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-stone-500 hover:text-stone-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <h1 className="font-heading font-bold text-xl text-stone-900">Edit Listing</h1>
        <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-500 hover:text-red-600 hover:bg-red-50">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div>
          <Label htmlFor="title">Title *</Label>
          <Input id="title" required placeholder="e.g. Premium Surfboard" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-1 h-12 rounded-xl" data-testid="listing-title-input" />
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" placeholder="Describe your item..."
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1 rounded-xl min-h-[120px]" data-testid="listing-description-input" />
        </div>

        {/* Category + Price */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-1 h-12 rounded-xl border-stone-200" data-testid="listing-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="price">Price per Day ($) *</Label>
            <Input id="price" type="number" min="1" step="0.01" required placeholder="25.00"
              value={form.price_per_day} onChange={(e) => setForm({ ...form, price_per_day: e.target.value })}
              className="mt-1 h-12 rounded-xl" data-testid="listing-price-input" />
          </div>
        </div>

        {/* Location */}
        <div className="relative" ref={locationInputRef}>
          <Label>Location *</Label>
          <div className="relative mt-1">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              type="text"
              placeholder="Search for a location..."
              value={locationQuery}
              onChange={handleLocationChange}
              onFocus={() => locationQuery.trim() && setShowLocationSuggestions(true)}
              className="h-12 pl-10 rounded-xl"
              data-testid="listing-location-input"
            />
          </div>
          {showLocationSuggestions && filteredLocations.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-lg border border-stone-200 max-h-64 overflow-auto">
              {filteredLocations.map((loc) => (
                <button
                  key={loc.name}
                  type="button"
                  onClick={() => selectLocation(loc)}
                  className="w-full px-4 py-3 text-left hover:bg-stone-50 flex items-center gap-2 text-sm"
                >
                  <MapPin className="w-4 h-4 text-stone-400" />
                  <span>{loc.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Delivery + Condition */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Delivery Option</Label>
            <Select value={form.delivery_option} onValueChange={(v) => setForm({ ...form, delivery_option: v })}>
              <SelectTrigger className="mt-1 h-12 rounded-xl border-stone-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pickup">Pickup Only</SelectItem>
                <SelectItem value="delivery">Delivery Only</SelectItem>
                <SelectItem value="both">Pickup or Delivery</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Condition</Label>
            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
              <SelectTrigger className="mt-1 h-12 rounded-xl border-stone-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Like New">Like New</SelectItem>
                <SelectItem value="Excellent">Excellent</SelectItem>
                <SelectItem value="Good">Good</SelectItem>
                <SelectItem value="Fair">Fair</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* For Sale Option */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Checkbox
              id="for_sale"
              checked={form.for_sale}
              onCheckedChange={(checked) => setForm({ ...form, for_sale: checked, sale_price: checked ? form.sale_price : "" })}
              className="mt-0.5"
              data-testid="for-sale-checkbox"
            />
            <div className="flex-1">
              <Label htmlFor="for_sale" className="text-sm font-medium text-stone-900 cursor-pointer">
                Also list this item for sale
              </Label>
              <p className="text-xs text-stone-500 mt-0.5">
                Allow buyers to purchase this item outright (7% deducted from your payout)
              </p>
            </div>
          </div>
          {form.for_sale && (
            <div className="pl-6">
              <Label htmlFor="sale_price" className="text-xs text-stone-600">Sale Price ($)</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="sale_price"
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="100.00"
                  value={form.sale_price}
                  onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                  className="h-10 rounded-lg pl-9"
                  data-testid="sale-price-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Images */}
        <div>
          <Label className="mb-2 block">Photos ({form.images.length}/5)</Label>
          
          {/* Image Preview Grid */}
          <div className="grid grid-cols-5 gap-3 mb-4">
            {form.images.map((img, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-stone-200 bg-stone-50">
                <img src={img} alt={`Item ${i + 1}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 p-1 bg-white/90 rounded-full shadow hover:bg-red-50 transition-colors">
                  <X className="w-3 h-3 text-red-500" />
                </button>
              </div>
            ))}
            {form.images.length < 5 && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="aspect-square rounded-xl border-2 border-dashed border-stone-200 flex flex-col items-center justify-center gap-1 hover:border-coral hover:bg-coral-light/50 transition-all disabled:opacity-50"
              >
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-stone-300 border-t-primary rounded-full animate-spin" />
                ) : (
                  <>
                    <ImagePlus className="w-5 h-5 text-stone-400" />
                    <span className="text-[10px] text-stone-400">Add</span>
                  </>
                )}
              </button>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" />
        </div>

        <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-coral hover:bg-coral text-white btn-press" data-testid="save-listing-btn">
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Saving...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-4 h-4" /> Save Changes
            </span>
          )}
        </Button>
      </form>
    </div>
  );
}
